import React, { useState, useEffect } from 'react';
import {
  useSalesReport,
  useDeletionsReport,
  useTargetsReport,
  useHourlyReport,
  useAuditReport,
  useTaxReport,
  useExecutiveSummary,
  formatCurrency,
  formatPercentage,
  formatDate,
  formatHour,
  getDateRange,
  SalesReport,
  DeletionsReport,
  TargetsReport,
  HourlyReport,
  AuditReport,
  TaxReport,
  ExecutiveSummary,
} from '../hooks/useReports';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from 'recharts';

// ==================== SVG ICONS ====================

const ChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 20V10M12 20V4M6 20v-6"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const TargetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const BankIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
  </svg>
);

const ClipboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const InboxIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
  </svg>
);

const TrendUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
  </svg>
);

const PackageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const BillIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 9v6M9 12h6"/>
  </svg>
);

const ReceiptIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z"/><path d="M8 10h8M8 14h8"/>
  </svg>
);

const CardsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const LineChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
  </svg>
);

type ReportType = 'summary' | 'sales' | 'deletions' | 'targets' | 'hourly' | 'audit' | 'tax';
type DatePreset = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export const InformesPage: React.FC = () => {
  const [activeReport, setActiveReport] = useState<ReportType>('summary');
  const [datePreset, setDatePreset] = useState<DatePreset>('month');

  // Fechas aplicadas (las que se usan para cargar reportes)
  const [appliedStartDate, setAppliedStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [appliedEndDate, setAppliedEndDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [appliedSelectedDate, setAppliedSelectedDate] = useState<Date>(new Date());

  // Helper para obtener fecha local en formato YYYY-MM-DD
  const getLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fechas temporales (las que el usuario está editando en los inputs)
  const [tempStartDate, setTempStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return getLocalDateString(d);
  });
  const [tempEndDate, setTempEndDate] = useState<string>(() => {
    return getLocalDateString(new Date());
  });
  const [tempSelectedDate, setTempSelectedDate] = useState<string>(() => {
    return getLocalDateString(new Date());
  });

  // Hooks para cada tipo de reporte
  const { fetchReport: fetchSales, report: salesReport, loading: salesLoading, error: salesError } = useSalesReport();
  const { fetchReport: fetchDeletions, report: deletionsReport, loading: deletionsLoading, error: deletionsError } = useDeletionsReport();
  const { fetchReport: fetchTargets, report: targetsReport, loading: targetsLoading, error: targetsError } = useTargetsReport();
  const { fetchReport: fetchHourly, report: hourlyReport, loading: hourlyLoading, error: hourlyError } = useHourlyReport();
  const { fetchReport: fetchAudit, report: auditReport, loading: auditLoading, error: auditError } = useAuditReport();
  const { fetchReport: fetchTax, report: taxReport, loading: taxLoading, error: taxError } = useTaxReport();
  const { fetchReport: fetchSummary, report: summaryReport, loading: summaryLoading, error: summaryError } = useExecutiveSummary();

  // Obtener el error actual según el reporte activo
  const getCurrentError = () => {
    switch (activeReport) {
      case 'summary': return summaryError;
      case 'sales': return salesError;
      case 'deletions': return deletionsError;
      case 'targets': return targetsError;
      case 'hourly': return hourlyError;
      case 'audit': return auditError;
      case 'tax': return taxError;
      default: return null;
    }
  };

  const currentError = getCurrentError();

  // Actualizar fechas temporales cuando cambia el preset (excepto custom)
  useEffect(() => {
    if (datePreset !== 'custom') {
      const { start, end } = getDateRange(datePreset);
      setTempStartDate(getLocalDateString(start));
      setTempEndDate(getLocalDateString(end));
      // Aplicar inmediatamente cuando se selecciona un preset
      setAppliedStartDate(start);
      setAppliedEndDate(end);
    }
  }, [datePreset]);

  // Cargar el reporte activo cuando cambian las fechas APLICADAS o el tipo de reporte
  useEffect(() => {
    loadReport();
  }, [activeReport, appliedStartDate, appliedEndDate, appliedSelectedDate]);

  const loadReport = async () => {
    // Validar que las fechas sean válidas antes de cargar
    const isValidDate = (d: Date) => d instanceof Date && !isNaN(d.getTime());

    if (activeReport === 'hourly') {
      if (!isValidDate(appliedSelectedDate)) return;
      await fetchHourly(appliedSelectedDate);
      return;
    }

    // Para todos los demás reportes, validar startDate y endDate
    if (!isValidDate(appliedStartDate) || !isValidDate(appliedEndDate)) return;

    switch (activeReport) {
      case 'summary':
        await fetchSummary(appliedStartDate, appliedEndDate);
        break;
      case 'sales':
        await fetchSales(appliedStartDate, appliedEndDate);
        break;
      case 'deletions':
        await fetchDeletions(appliedStartDate, appliedEndDate);
        break;
      case 'targets':
        await fetchTargets(appliedStartDate, appliedEndDate);
        break;
      case 'audit':
        await fetchAudit(appliedStartDate, appliedEndDate);
        break;
      case 'tax':
        await fetchTax(appliedStartDate, appliedEndDate);
        break;
    }
  };

  // Aplicar fechas temporales al presionar "Actualizar"
  const applyDatesAndLoad = () => {
    if (activeReport === 'hourly') {
      if (tempSelectedDate) {
        const newDate = new Date(tempSelectedDate + 'T00:00:00');
        if (!isNaN(newDate.getTime())) {
          setAppliedSelectedDate(newDate);
        }
      }
    } else {
      if (tempStartDate && tempEndDate) {
        const newStart = new Date(tempStartDate + 'T00:00:00');
        const newEnd = new Date(tempEndDate + 'T23:59:59');
        if (!isNaN(newStart.getTime()) && !isNaN(newEnd.getTime())) {
          setAppliedStartDate(newStart);
          setAppliedEndDate(newEnd);
        }
      }
    }
  };

  const isLoading = salesLoading || deletionsLoading || targetsLoading || hourlyLoading || auditLoading || taxLoading || summaryLoading;

  const reportTabs: { key: ReportType; label: string; icon: React.ReactNode }[] = [
    { key: 'summary', label: 'Resumen Ejecutivo', icon: <ChartIcon /> },
    { key: 'sales', label: 'Ventas', icon: <DollarIcon /> },
    { key: 'deletions', label: 'Eliminaciones', icon: <TrashIcon /> },
    { key: 'targets', label: 'Cumplimiento de Metas', icon: <TargetIcon /> },
    { key: 'hourly', label: 'Ventas por Hora', icon: <ClockIcon /> },
    { key: 'tax', label: 'Impuestos', icon: <BankIcon /> },
    { key: 'audit', label: 'Auditoría', icon: <ClipboardIcon /> },
  ];

  return (
    <div className="informes-container">
      <style>{styles}</style>

      {/* Header */}
      <div className="informes-header">
        <div className="informes-header-content">
          <div className="informes-header-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4zm2 2H5V5h14v14.01zM19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h1>Informes y Reportes</h1>
            <p>Consulta y analiza los datos del sistema</p>
          </div>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="informes-tabs">
        {reportTabs.map(tab => (
          <button
            key={tab.key}
            className={`informes-tab ${activeReport === tab.key ? 'active' : ''}`}
            onClick={() => setActiveReport(tab.key as ReportType)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Date Filters */}
      <div className="informes-filters">
        {activeReport !== 'hourly' ? (
          <>
            <div className="filter-presets">
              {(['today', 'week', 'month', 'quarter', 'year', 'custom'] as DatePreset[]).map(preset => (
                <button
                  key={preset}
                  className={`preset-btn ${datePreset === preset ? 'active' : ''}`}
                  onClick={() => setDatePreset(preset)}
                >
                  {preset === 'today' && 'Hoy'}
                  {preset === 'week' && 'Semana'}
                  {preset === 'month' && 'Mes'}
                  {preset === 'quarter' && 'Trimestre'}
                  {preset === 'year' && 'Año'}
                  {preset === 'custom' && 'Personalizado'}
                </button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <div className="filter-dates">
                <div className="date-input-group">
                  <label>Desde:</label>
                  <input
                    type="date"
                    value={tempStartDate}
                    onChange={e => setTempStartDate(e.target.value)}
                  />
                </div>
                <div className="date-input-group">
                  <label>Hasta:</label>
                  <input
                    type="date"
                    value={tempEndDate}
                    onChange={e => setTempEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="filter-dates">
            <div className="date-input-group">
              <label>Fecha:</label>
              <input
                type="date"
                value={tempSelectedDate}
                onChange={e => setTempSelectedDate(e.target.value)}
              />
            </div>
          </div>
        )}
        <button className="refresh-btn" onClick={applyDatesAndLoad} disabled={isLoading}>
          {isLoading ? 'Cargando...' : 'Actualizar'}
        </button>
      </div>

      {/* Report Content */}
      <div className="informes-content">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span>Generando reporte...</span>
          </div>
        )}

        {/* Mostrar error si existe */}
        {currentError && !isLoading && (
          <div className="error-message">
            <div className="error-icon"><AlertIcon /></div>
            <h3>Error al cargar el reporte</h3>
            <p>{currentError.message}</p>
            <button className="retry-btn" onClick={loadReport}>
              Reintentar
            </button>
          </div>
        )}

        {activeReport === 'summary' && !isLoading && !summaryError && (
          summaryReport ? <ExecutiveSummaryReport report={summaryReport} /> : <NoDataMessage type="resumen ejecutivo" />
        )}
        {activeReport === 'sales' && !isLoading && !salesError && (
          salesReport ? <SalesReportView report={salesReport} /> : <NoDataMessage type="ventas" />
        )}
        {activeReport === 'deletions' && !isLoading && !deletionsError && (
          deletionsReport ? <DeletionsReportView report={deletionsReport} /> : <NoDataMessage type="eliminaciones" />
        )}
        {activeReport === 'targets' && !isLoading && !targetsError && (
          targetsReport ? <TargetsReportView report={targetsReport} /> : <NoDataMessage type="cumplimiento de metas" />
        )}
        {activeReport === 'hourly' && !isLoading && !hourlyError && (
          hourlyReport ? <HourlyReportView report={hourlyReport} /> : <NoDataMessage type="ventas por hora" />
        )}
        {activeReport === 'tax' && !isLoading && !taxError && (
          taxReport ? <TaxReportView report={taxReport} /> : <NoDataMessage type="impuestos" />
        )}
        {activeReport === 'audit' && !isLoading && !auditError && (
          auditReport ? <AuditReportView report={auditReport} /> : <NoDataMessage type="auditoría" />
        )}
      </div>
    </div>
  );
};

// ==================== REPORT COMPONENTS ====================

const NoDataMessage: React.FC<{ type: string }> = ({ type }) => (
  <div className="no-data-message">
    <div className="no-data-icon"><InboxIcon /></div>
    <h3>Sin datos para mostrar</h3>
    <p>No hay datos de {type} para el período seleccionado.</p>
    <p className="no-data-hint">Intenta seleccionar un rango de fechas diferente o presiona "Actualizar".</p>
  </div>
);

const ExecutiveSummaryReport: React.FC<{ report: ExecutiveSummary }> = ({ report }) => {
  const [viewMode, setViewMode] = useState<'cards' | 'charts'>('cards');

  // Colores para gráficos
  const COLORS = {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    purple: '#8b5cf6',
    pink: '#ec4899',
    cyan: '#06b6d4',
    indigo: '#6366f1',
  };

  // Datos para el gráfico de distribución de ventas vs eliminaciones
  const salesVsDeletedData = [
    { name: 'Ventas Netas', value: report.sales.total - report.deletions.amount, fill: COLORS.success },
    { name: 'Eliminaciones', value: report.deletions.amount, fill: COLORS.danger },
  ];

  // Datos para el gráfico de cumplimiento de metas
  const targetsData = [
    { name: 'Días con Meta', value: report.targets.daysMetTarget, fill: COLORS.success },
    { name: 'Días Sin Meta', value: report.targets.daysTotal - report.targets.daysMetTarget, fill: COLORS.warning },
  ];

  // Datos para el gráfico radial de cumplimiento
  const completionRadialData = [
    {
      name: 'Cumplimiento',
      value: Math.min(report.targets.averageCompletion, 100),
      fill: report.targets.averageCompletion >= 100 ? COLORS.success : report.targets.averageCompletion >= 80 ? COLORS.warning : COLORS.danger,
    },
  ];


  // Datos para distribución de impuestos
  const taxBreakdownData = [
    { name: 'Base Imponible', value: report.sales.total - report.taxes.collected, fill: COLORS.primary },
    { name: 'Impuestos', value: report.taxes.collected, fill: COLORS.purple },
  ];

  // Formatter para tooltip
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatTooltipValue = (value: any) => {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    return formatCurrency(num);
  };

  // KPIs calculados
  const netSales = report.sales.total - report.deletions.amount;
  const deletionImpact = report.deletions.percentage;
  const avgInvoicePerDay = report.period.days > 0 ? report.sales.invoiceCount / report.period.days : 0;

  return (
    <div className="report-section executive-summary-enhanced">
      {/* Header con toggle de vista */}
      <div className="summary-header">
        <div className="summary-header-left">
          <h2>Resumen Ejecutivo</h2>
          <p className="report-period">
            Período: {formatDate(report.period.start)} - {formatDate(report.period.end)} ({report.period.days} días)
          </p>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            <span className="toggle-icon"><CardsIcon /></span>
            <span>Tarjetas</span>
          </button>
          <button
            className={`toggle-btn ${viewMode === 'charts' ? 'active' : ''}`}
            onClick={() => setViewMode('charts')}
          >
            <span className="toggle-icon"><LineChartIcon /></span>
            <span>Gráficos</span>
          </button>
        </div>
      </div>

      {/* Vista de Tarjetas */}
      {viewMode === 'cards' && (
        <>
          {/* KPIs principales - Primera fila */}
          <div className="kpi-row">
            <div className="kpi-card kpi-sales">
              <div className="kpi-icon-wrapper">
                <div className="kpi-icon"><DollarIcon /></div>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Ventas Totales</span>
                <span className="kpi-value">{formatCurrency(report.sales.total)}</span>
                <div className="kpi-mini-chart">
                  <ResponsiveContainer width="100%" height={40}>
                    <AreaChart data={[{ v: 0 }, { v: report.sales.total * 0.3 }, { v: report.sales.total * 0.5 }, { v: report.sales.total * 0.7 }, { v: report.sales.total }]}>
                      <defs>
                        <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke={COLORS.success} fill="url(#salesGradient)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="kpi-badge success">+{report.sales.invoiceCount} facturas</div>
            </div>

            <div className="kpi-card kpi-net">
              <div className="kpi-icon-wrapper net">
                <div className="kpi-icon"><TrendUpIcon /></div>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Ventas Netas</span>
                <span className="kpi-value">{formatCurrency(netSales)}</span>
                <span className="kpi-subtitle">Después de eliminaciones</span>
              </div>
              <div className="kpi-badge info">{formatPercentage(100 - deletionImpact)} efectivo</div>
            </div>

            <div className="kpi-card kpi-deletions">
              <div className="kpi-icon-wrapper warning">
                <div className="kpi-icon"><TrashIcon /></div>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Eliminaciones</span>
                <span className="kpi-value warning">{formatCurrency(report.deletions.amount)}</span>
                <span className="kpi-subtitle">{report.deletions.count} facturas eliminadas</span>
              </div>
              <div className={`kpi-badge ${deletionImpact > 10 ? 'danger' : deletionImpact > 5 ? 'warning' : 'success'}`}>
                {formatPercentage(deletionImpact)} del total
              </div>
            </div>

            <div className="kpi-card kpi-targets">
              <div className="kpi-icon-wrapper primary">
                <div className="kpi-icon"><TargetIcon /></div>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Cumplimiento</span>
                <span className={`kpi-value ${report.targets.averageCompletion >= 100 ? 'success' : report.targets.averageCompletion >= 80 ? '' : 'warning'}`}>
                  {formatPercentage(report.targets.averageCompletion)}
                </span>
                <div className="progress-bar-mini">
                  <div
                    className="progress-fill-mini"
                    style={{
                      width: `${Math.min(report.targets.averageCompletion, 100)}%`,
                      backgroundColor: report.targets.averageCompletion >= 100 ? COLORS.success : report.targets.averageCompletion >= 80 ? COLORS.warning : COLORS.danger
                    }}
                  />
                </div>
              </div>
              <div className="kpi-badge primary">{report.targets.daysMetTarget}/{report.targets.daysTotal} días</div>
            </div>
          </div>

          {/* Segunda fila - Métricas detalladas */}
          <div className="metrics-grid">
            {/* Desglose de Ventas */}
            <div className="metric-card">
              <h4>Análisis de Ventas</h4>
              <div className="metric-chart-container">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={salesVsDeletedData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {salesVsDeletedData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={formatTooltipValue} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="metric-footer">
                <div className="metric-stat">
                  <span className="stat-dot success"></span>
                  <span>Netas: {formatCurrency(netSales)}</span>
                </div>
                <div className="metric-stat">
                  <span className="stat-dot danger"></span>
                  <span>Elim: {formatCurrency(report.deletions.amount)}</span>
                </div>
              </div>
            </div>

            {/* Cumplimiento de Metas */}
            <div className="metric-card">
              <h4>Cumplimiento de Metas</h4>
              <div className="metric-chart-container">
                <ResponsiveContainer width="100%" height={180}>
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    barSize={15}
                    data={completionRadialData}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar
                      background={{ fill: '#e5e7eb' }}
                      dataKey="value"
                      cornerRadius={10}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="radial-center-label">
                  <span className="radial-value">{formatPercentage(report.targets.averageCompletion)}</span>
                  <span className="radial-text">Promedio</span>
                </div>
              </div>
              <div className="metric-footer">
                <div className="metric-stat">
                  <span className="stat-dot success"></span>
                  <span>Con meta: {report.targets.daysMetTarget} días</span>
                </div>
                <div className="metric-stat">
                  <span className="stat-dot warning"></span>
                  <span>Sin meta: {report.targets.daysTotal - report.targets.daysMetTarget} días</span>
                </div>
              </div>
            </div>

            {/* Impuestos */}
            <div className="metric-card">
              <h4>Distribución Fiscal</h4>
              <div className="metric-chart-container">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={taxBreakdownData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {taxBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={formatTooltipValue} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="metric-footer">
                <div className="metric-stat">
                  <span className="stat-dot primary"></span>
                  <span>Impuestos: {formatCurrency(report.taxes.collected)}</span>
                </div>
                <div className="metric-stat">
                  <span className="stat-dot purple"></span>
                  <span>Tasa: {formatPercentage(report.taxes.effectiveRate)}</span>
                </div>
              </div>
            </div>

            {/* Métricas de Productividad */}
            <div className="metric-card">
              <h4>Productividad</h4>
              <div className="productivity-metrics">
                <div className="prod-metric">
                  <div className="prod-icon"><PackageIcon /></div>
                  <div className="prod-content">
                    <span className="prod-value">{avgInvoicePerDay.toFixed(1)}</span>
                    <span className="prod-label">Facturas/día</span>
                  </div>
                </div>
                <div className="prod-metric">
                  <div className="prod-icon"><BillIcon /></div>
                  <div className="prod-content">
                    <span className="prod-value">{formatCurrency(report.sales.averageDaily)}</span>
                    <span className="prod-label">Promedio diario</span>
                  </div>
                </div>
                <div className="prod-metric">
                  <div className="prod-icon"><ReceiptIcon /></div>
                  <div className="prod-content">
                    <span className="prod-value">{formatCurrency(report.sales.averageTicket)}</span>
                    <span className="prod-label">Ticket promedio</span>
                  </div>
                </div>
                <div className="prod-metric">
                  <div className="prod-icon"><ChartIcon /></div>
                  <div className="prod-content">
                    <span className="prod-value">{report.period.days}</span>
                    <span className="prod-label">Días analizados</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Vista de Gráficos */}
      {viewMode === 'charts' && (
        <>
          {/* Gráficos grandes */}
          <div className="charts-grid">
            {/* Gráfico de barras comparativo */}
            <div className="chart-card full-width">
              <h4>Resumen Financiero</h4>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: 'Ventas Brutas', valor: report.sales.total },
                      { name: 'Ventas Netas', valor: netSales },
                      { name: 'Eliminaciones', valor: report.deletions.amount },
                      { name: 'Impuestos', valor: report.taxes.collected },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip formatter={formatTooltipValue} />
                    <Bar dataKey="valor" radius={[8, 8, 0, 0]}>
                      <Cell fill={COLORS.primary} />
                      <Cell fill={COLORS.success} />
                      <Cell fill={COLORS.danger} />
                      <Cell fill={COLORS.purple} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gauge de Cumplimiento */}
            <div className="chart-card">
              <h4>Nivel de Cumplimiento</h4>
              <div className="chart-container gauge-container">
                <ResponsiveContainer width="100%" height={250}>
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="40%"
                    outerRadius="80%"
                    barSize={20}
                    data={[
                      { name: 'Meta', value: 100, fill: '#e5e7eb' },
                      { ...completionRadialData[0], value: Math.min(completionRadialData[0].value, 100) }
                    ]}
                    startAngle={180}
                    endAngle={-180}
                  >
                    <RadialBar dataKey="value" cornerRadius={10} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="gauge-center">
                  <span className="gauge-value">{formatPercentage(report.targets.averageCompletion)}</span>
                  <span className="gauge-label">Cumplimiento</span>
                </div>
              </div>
            </div>

            {/* Distribución de días */}
            <div className="chart-card">
              <h4>Días vs Metas</h4>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={targetsData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {targetsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Indicadores de rendimiento */}
          <div className="performance-indicators">
            <div className="perf-indicator">
              <div className="perf-header">
                <span className="perf-title">Eficiencia de Ventas</span>
                <span className={`perf-value ${100 - deletionImpact >= 90 ? 'success' : 'warning'}`}>
                  {formatPercentage(100 - deletionImpact)}
                </span>
              </div>
              <div className="perf-bar">
                <div
                  className="perf-fill"
                  style={{
                    width: `${100 - deletionImpact}%`,
                    backgroundColor: 100 - deletionImpact >= 90 ? COLORS.success : COLORS.warning
                  }}
                />
              </div>
              <span className="perf-desc">Porcentaje de ventas efectivas vs totales</span>
            </div>

            <div className="perf-indicator">
              <div className="perf-header">
                <span className="perf-title">Cumplimiento de Metas</span>
                <span className={`perf-value ${report.targets.averageCompletion >= 100 ? 'success' : report.targets.averageCompletion >= 80 ? 'warning' : 'danger'}`}>
                  {formatPercentage(report.targets.averageCompletion)}
                </span>
              </div>
              <div className="perf-bar">
                <div
                  className="perf-fill"
                  style={{
                    width: `${Math.min(report.targets.averageCompletion, 100)}%`,
                    backgroundColor: report.targets.averageCompletion >= 100 ? COLORS.success : report.targets.averageCompletion >= 80 ? COLORS.warning : COLORS.danger
                  }}
                />
              </div>
              <span className="perf-desc">Promedio de cumplimiento diario</span>
            </div>

            <div className="perf-indicator">
              <div className="perf-header">
                <span className="perf-title">Tasa Fiscal Efectiva</span>
                <span className="perf-value">{formatPercentage(report.taxes.effectiveRate)}</span>
              </div>
              <div className="perf-bar">
                <div
                  className="perf-fill"
                  style={{ width: `${Math.min(report.taxes.effectiveRate * 5, 100)}%`, backgroundColor: COLORS.purple }}
                />
              </div>
              <span className="perf-desc">Impuestos recaudados vs ventas totales</span>
            </div>

            <div className="perf-indicator">
              <div className="perf-header">
                <span className="perf-title">Días con Meta Alcanzada</span>
                <span className={`perf-value ${(report.targets.daysMetTarget / report.targets.daysTotal) >= 0.8 ? 'success' : 'warning'}`}>
                  {report.targets.daysTotal > 0 ? formatPercentage((report.targets.daysMetTarget / report.targets.daysTotal) * 100) : '0%'}
                </span>
              </div>
              <div className="perf-bar">
                <div
                  className="perf-fill"
                  style={{
                    width: `${report.targets.daysTotal > 0 ? (report.targets.daysMetTarget / report.targets.daysTotal) * 100 : 0}%`,
                    backgroundColor: (report.targets.daysMetTarget / report.targets.daysTotal) >= 0.8 ? COLORS.success : COLORS.warning
                  }}
                />
              </div>
              <span className="perf-desc">{report.targets.daysMetTarget} de {report.targets.daysTotal} días cumplieron la meta</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SalesReportView: React.FC<{ report: SalesReport }> = ({ report }) => (
  <div className="report-section">
    <h2>Reporte de Ventas</h2>

    <div className="metrics-row">
      <div className="metric-box">
        <span className="metric-label">Total Ventas</span>
        <span className="metric-value">{formatCurrency(report.totalSales)}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Total Facturas</span>
        <span className="metric-value">{report.totalInvoices.toLocaleString()}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Ticket Promedio</span>
        <span className="metric-value">{formatCurrency(report.averageTicket)}</span>
      </div>
    </div>

    <div className="breakdown-section">
      <div className="breakdown-card">
        <h4>Facturas Electrónicas (DIAN)</h4>
        <div className="breakdown-value">{formatCurrency(report.electronicAmount)}</div>
        <div className="breakdown-count">{report.electronicInvoices.toLocaleString()} facturas</div>
      </div>
      <div className="breakdown-card">
        <h4>Facturas Internas</h4>
        <div className="breakdown-value">{formatCurrency(report.internalAmount)}</div>
        <div className="breakdown-count">{report.internalInvoices.toLocaleString()} facturas</div>
      </div>
    </div>

    {report.dailyBreakdown.length > 0 && (
      <div className="table-section">
        <h3>Detalle Diario</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Ventas</th>
              <th>Facturas</th>
              <th>Electrónicas</th>
              <th>Internas</th>
            </tr>
          </thead>
          <tbody>
            {report.dailyBreakdown.map(day => (
              <tr key={day.date}>
                <td>{formatDate(day.date)}</td>
                <td className="amount">{formatCurrency(day.totalSales)}</td>
                <td>{day.invoiceCount}</td>
                <td>{day.electronicCount}</td>
                <td>{day.internalCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const DeletionsReportView: React.FC<{ report: DeletionsReport }> = ({ report }) => (
  <div className="report-section">
    <h2>Reporte de Eliminaciones</h2>

    <div className="metrics-row">
      <div className="metric-box warning">
        <span className="metric-label">Total Eliminado</span>
        <span className="metric-value">{formatCurrency(report.totalAmount)}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Facturas Eliminadas</span>
        <span className="metric-value">{report.totalDeleted.toLocaleString()}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Automáticas</span>
        <span className="metric-value">{report.automaticDeletions.toLocaleString()}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Manuales</span>
        <span className="metric-value">{report.manualDeletions.toLocaleString()}</span>
      </div>
    </div>

    {report.deletionsByUser.length > 0 && (
      <div className="table-section">
        <h3>Por Usuario</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Cantidad</th>
              <th>Monto</th>
            </tr>
          </thead>
          <tbody>
            {report.deletionsByUser.map(user => (
              <tr key={user.userId}>
                <td>{user.username}</td>
                <td>{user.count}</td>
                <td className="amount">{formatCurrency(user.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {report.deletionsByDay.length > 0 && (
      <div className="table-section">
        <h3>Por Día</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cantidad</th>
              <th>Monto</th>
            </tr>
          </thead>
          <tbody>
            {report.deletionsByDay.map(day => (
              <tr key={day.date}>
                <td>{formatDate(day.date)}</td>
                <td>{day.count}</td>
                <td className="amount">{formatCurrency(day.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const TargetsReportView: React.FC<{ report: TargetsReport }> = ({ report }) => (
  <div className="report-section">
    <h2>Reporte de Cumplimiento de Metas</h2>

    <div className="metrics-row">
      <div className="metric-box">
        <span className="metric-label">Cumplimiento Promedio</span>
        <span className={`metric-value ${report.averageCompletion >= 100 ? 'success' : report.averageCompletion >= 80 ? '' : 'warning'}`}>
          {formatPercentage(report.averageCompletion)}
        </span>
      </div>
      <div className="metric-box success">
        <span className="metric-label">Días con Meta</span>
        <span className="metric-value">{report.daysMetTarget}</span>
      </div>
      <div className="metric-box warning">
        <span className="metric-label">Días Sin Meta</span>
        <span className="metric-value">{report.daysBelowTarget}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Días Analizados</span>
        <span className="metric-value">{report.daysAnalyzed}</span>
      </div>
    </div>

    <div className="totals-section">
      <div className="total-box">
        <span>Meta Total:</span>
        <strong>{formatCurrency(report.totalTarget)}</strong>
      </div>
      <div className="total-box">
        <span>Real Total:</span>
        <strong>{formatCurrency(report.totalActual)}</strong>
      </div>
      <div className={`total-box ${report.totalActual >= report.totalTarget ? 'success' : 'warning'}`}>
        <span>Diferencia:</span>
        <strong>{formatCurrency(report.totalActual - report.totalTarget)}</strong>
      </div>
    </div>

    {report.dailyDetails.length > 0 && (
      <div className="table-section">
        <h3>Detalle Diario</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Meta</th>
              <th>Real</th>
              <th>Cumplimiento</th>
              <th>Diferencia</th>
            </tr>
          </thead>
          <tbody>
            {report.dailyDetails.map(day => (
              <tr key={day.date} className={day.completion >= 100 ? 'row-success' : day.completion >= 80 ? '' : 'row-warning'}>
                <td>{formatDate(day.date)}</td>
                <td className="amount">{formatCurrency(day.target)}</td>
                <td className="amount">{formatCurrency(day.actual)}</td>
                <td className={day.completion >= 100 ? 'success' : day.completion >= 80 ? '' : 'warning'}>
                  {formatPercentage(day.completion)}
                </td>
                <td className={`amount ${day.difference >= 0 ? 'success' : 'warning'}`}>
                  {formatCurrency(day.difference)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const HourlyReportView: React.FC<{ report: HourlyReport }> = ({ report }) => (
  <div className="report-section">
    <h2>Reporte de Ventas por Hora</h2>
    <p className="report-period">Fecha: {formatDate(report.date)}</p>

    <div className="metrics-row">
      <div className="metric-box">
        <span className="metric-label">Total del Día</span>
        <span className="metric-value">{formatCurrency(report.totalSales)}</span>
      </div>
      <div className="metric-box success">
        <span className="metric-label">Hora Pico</span>
        <span className="metric-value">{formatHour(report.peakHour)}</span>
        <span className="metric-sub">{formatCurrency(report.peakSales)}</span>
      </div>
      <div className="metric-box warning">
        <span className="metric-label">Hora Baja</span>
        <span className="metric-value">{formatHour(report.lowHour)}</span>
        <span className="metric-sub">{formatCurrency(report.lowSales)}</span>
      </div>
    </div>

    <div className="hourly-chart">
      {report.hourlyBreakdown.filter(h => h.sales > 0 || (h.hour >= 6 && h.hour <= 22)).map(hour => (
        <div key={hour.hour} className="hour-bar-container">
          <span className="hour-label">{formatHour(hour.hour)}</span>
          <div className="hour-bar-wrapper">
            <div
              className="hour-bar"
              style={{
                width: `${Math.max(hour.percentage, 2)}%`,
                backgroundColor: hour.hour === report.peakHour ? '#10b981' : hour.hour === report.lowHour ? '#f59e0b' : '#3b82f6',
              }}
            />
            <span className="hour-value">{formatCurrency(hour.sales)}</span>
          </div>
          <span className="hour-count">{hour.invoiceCount} facturas</span>
        </div>
      ))}
    </div>
  </div>
);

const TaxReportView: React.FC<{ report: TaxReport }> = ({ report }) => (
  <div className="report-section">
    <h2>Reporte de Impuestos</h2>

    <div className="metrics-row">
      <div className="metric-box">
        <span className="metric-label">Impuesto Recaudado</span>
        <span className="metric-value">{formatCurrency(report.totalTaxCollected)}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Subtotal (Base)</span>
        <span className="metric-value">{formatCurrency(report.totalSubtotal)}</span>
      </div>
      <div className="metric-box">
        <span className="metric-label">Tasa Efectiva</span>
        <span className="metric-value">{formatPercentage(report.effectiveTaxRate)}</span>
      </div>
    </div>

    {report.dailyBreakdown.length > 0 && (
      <div className="table-section">
        <h3>Detalle Diario</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Subtotal</th>
              <th>Impuesto</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {report.dailyBreakdown.map(day => (
              <tr key={day.date}>
                <td>{formatDate(day.date)}</td>
                <td className="amount">{formatCurrency(day.subtotal)}</td>
                <td className="amount">{formatCurrency(day.tax)}</td>
                <td className="amount">{formatCurrency(day.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const AuditReportView: React.FC<{ report: AuditReport }> = ({ report }) => (
  <div className="report-section">
    <h2>Reporte de Auditoría</h2>

    <div className="metrics-row">
      <div className="metric-box">
        <span className="metric-label">Total Acciones</span>
        <span className="metric-value">{report.totalActions.toLocaleString()}</span>
      </div>
    </div>

    <div className="audit-grid">
      {report.actionsByModule.length > 0 && (
        <div className="audit-section">
          <h3>Por Módulo</h3>
          <div className="audit-list">
            {report.actionsByModule.slice(0, 10).map(item => (
              <div key={item.module} className="audit-item">
                <span className="audit-label">{item.module}</span>
                <span className="audit-count">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.actionsByUser.length > 0 && (
        <div className="audit-section">
          <h3>Por Usuario</h3>
          <div className="audit-list">
            {report.actionsByUser.slice(0, 10).map(item => (
              <div key={item.userId} className="audit-item">
                <span className="audit-label">{item.username}</span>
                <span className="audit-count">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.actionsByType.length > 0 && (
        <div className="audit-section">
          <h3>Por Tipo de Acción</h3>
          <div className="audit-list">
            {report.actionsByType.slice(0, 10).map(item => (
              <div key={item.action} className="audit-item">
                <span className="audit-label">{item.action}</span>
                <span className="audit-count">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    {report.recentActions.length > 0 && (
      <div className="table-section">
        <h3>Últimas Acciones</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Fecha/Hora</th>
              <th>Acción</th>
              <th>Módulo</th>
              <th>Usuario</th>
            </tr>
          </thead>
          <tbody>
            {report.recentActions.slice(0, 20).map(action => (
              <tr key={action.id}>
                <td>{new Date(action.createdAt).toLocaleString('es-CO')}</td>
                <td>{action.action}</td>
                <td>{action.module}</td>
                <td>{action.userId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ==================== STYLES ====================

const styles = `
  .informes-container {
    padding: 0;
  }

  .informes-header {
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    padding: 2rem;
    border-radius: 16px;
    margin-bottom: 1.5rem;
    color: white;
  }

  .informes-header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .informes-header-icon {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .informes-header-icon svg {
    width: 32px;
    height: 32px;
  }

  .informes-header h1 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
  }

  .informes-header p {
    margin: 0.25rem 0 0;
    opacity: 0.9;
  }

  .informes-tabs {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem;
    background: var(--bg-secondary, #f1f5f9);
    border-radius: 12px;
    margin-bottom: 1.5rem;
    overflow-x: auto;
  }

  .informes-tab {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    color: var(--text-secondary, #64748b);
    transition: all 0.2s;
  }

  .informes-tab:hover {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  .informes-tab.active {
    background: white;
    color: #1e40af;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .tab-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tab-icon svg {
    width: 16px;
    height: 16px;
  }

  .tab-label {
    font-size: 0.9rem;
  }

  .informes-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--card-bg, white);
    border-radius: 12px;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .filter-presets {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .preset-btn {
    padding: 0.5rem 1rem;
    border: 1px solid var(--border-color, #e2e8f0);
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .preset-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  .preset-btn.active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;
  }

  .filter-dates {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .date-input-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .date-input-group label {
    font-size: 0.85rem;
    color: var(--text-secondary, #64748b);
  }

  .date-input-group input {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    font-size: 0.9rem;
  }

  .refresh-btn {
    margin-left: auto;
    padding: 0.5rem 1.5rem;
    background: linear-gradient(135deg, #3b82f6, #1e40af);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
  }

  .refresh-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .refresh-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .informes-content {
    background: var(--card-bg, white);
    border-radius: 16px;
    padding: 1.5rem;
    min-height: 400px;
    position: relative;
  }

  .loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    border-radius: 16px;
    z-index: 10;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .report-section h2 {
    margin: 0 0 1rem;
    font-size: 1.5rem;
    color: var(--text-primary, #1e293b);
  }

  .report-period {
    color: var(--text-secondary, #64748b);
    margin-bottom: 1.5rem;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .summary-card {
    background: var(--bg-secondary, #f8fafc);
    border-radius: 16px;
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
  }

  .summary-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
  }

  .sales-card::before { background: linear-gradient(90deg, #10b981, #34d399); }
  .deletions-card::before { background: linear-gradient(90deg, #ef4444, #f87171); }
  .targets-card::before { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
  .tax-card::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }

  .summary-card .card-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .summary-card h3 {
    margin: 0 0 0.5rem;
    font-size: 0.9rem;
    color: var(--text-secondary, #64748b);
    font-weight: 500;
  }

  .summary-card .card-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-primary, #1e293b);
    margin-bottom: 1rem;
  }

  .card-details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
  }

  .detail-row span {
    color: var(--text-secondary, #64748b);
  }

  .detail-row strong {
    color: var(--text-primary, #1e293b);
  }

  .detail-row .success { color: #10b981; }
  .detail-row .warning { color: #f59e0b; }

  .metrics-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .metric-box {
    flex: 1;
    min-width: 150px;
    background: var(--bg-secondary, #f8fafc);
    padding: 1rem 1.25rem;
    border-radius: 12px;
    text-align: center;
  }

  .metric-box.success { background: rgba(16, 185, 129, 0.1); }
  .metric-box.warning { background: rgba(245, 158, 11, 0.1); }

  .metric-label {
    display: block;
    font-size: 0.8rem;
    color: var(--text-secondary, #64748b);
    margin-bottom: 0.5rem;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary, #1e293b);
  }

  .metric-value.success { color: #10b981; }
  .metric-value.warning { color: #f59e0b; }

  .metric-sub {
    display: block;
    font-size: 0.8rem;
    color: var(--text-secondary, #64748b);
    margin-top: 0.25rem;
  }

  .breakdown-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .breakdown-card {
    background: var(--bg-secondary, #f8fafc);
    padding: 1.25rem;
    border-radius: 12px;
    text-align: center;
  }

  .breakdown-card h4 {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    color: var(--text-secondary, #64748b);
    font-weight: 500;
  }

  .breakdown-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--text-primary, #1e293b);
  }

  .breakdown-count {
    font-size: 0.85rem;
    color: var(--text-secondary, #64748b);
    margin-top: 0.25rem;
  }

  .totals-section {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: var(--bg-secondary, #f8fafc);
    border-radius: 12px;
  }

  .total-box {
    flex: 1;
    min-width: 150px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }

  .total-box span {
    font-size: 0.85rem;
    color: var(--text-secondary, #64748b);
  }

  .total-box strong {
    font-size: 1.1rem;
    color: var(--text-primary, #1e293b);
  }

  .total-box.success strong { color: #10b981; }
  .total-box.warning strong { color: #f59e0b; }

  .table-section {
    margin-top: 1.5rem;
  }

  .table-section h3 {
    margin: 0 0 1rem;
    font-size: 1.1rem;
    color: var(--text-primary, #1e293b);
  }

  .report-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }

  .report-table th,
  .report-table td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border-color, #e2e8f0);
  }

  .report-table th {
    background: var(--bg-secondary, #f8fafc);
    font-weight: 600;
    color: var(--text-secondary, #64748b);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .report-table td.amount {
    font-family: 'Roboto Mono', monospace;
    text-align: right;
  }

  .report-table td.success { color: #10b981; }
  .report-table td.warning { color: #f59e0b; }

  .report-table tr.row-success { background: rgba(16, 185, 129, 0.05); }
  .report-table tr.row-warning { background: rgba(245, 158, 11, 0.05); }

  .hourly-chart {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1.5rem;
  }

  .hour-bar-container {
    display: grid;
    grid-template-columns: 80px 1fr 100px;
    align-items: center;
    gap: 1rem;
  }

  .hour-label {
    font-size: 0.85rem;
    color: var(--text-secondary, #64748b);
    text-align: right;
  }

  .hour-bar-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 28px;
    background: var(--bg-secondary, #f1f5f9);
    border-radius: 4px;
    overflow: hidden;
  }

  .hour-bar {
    height: 100%;
    border-radius: 4px;
    min-width: 4px;
    transition: width 0.3s ease;
  }

  .hour-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    white-space: nowrap;
  }

  .hour-count {
    font-size: 0.8rem;
    color: var(--text-secondary, #64748b);
    text-align: right;
  }

  .audit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .audit-section {
    background: var(--bg-secondary, #f8fafc);
    padding: 1.25rem;
    border-radius: 12px;
  }

  .audit-section h3 {
    margin: 0 0 1rem;
    font-size: 1rem;
    color: var(--text-primary, #1e293b);
  }

  .audit-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .audit-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem;
    background: white;
    border-radius: 6px;
  }

  .audit-label {
    font-size: 0.85rem;
    color: var(--text-primary, #1e293b);
  }

  .audit-count {
    font-size: 0.85rem;
    font-weight: 600;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    .informes-tabs {
      justify-content: flex-start;
    }

    .tab-label {
      display: none;
    }

    .informes-tab.active .tab-label {
      display: inline;
    }

    .filter-presets {
      width: 100%;
    }

    .filter-dates {
      width: 100%;
      flex-direction: column;
    }

    .refresh-btn {
      width: 100%;
    }

    .hour-bar-container {
      grid-template-columns: 60px 1fr;
    }

    .hour-count {
      display: none;
    }
  }

  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .error-icon {
    margin-bottom: 1rem;
    color: #dc2626;
  }

  .error-icon svg {
    width: 48px;
    height: 48px;
  }

  .error-message h3 {
    margin: 0 0 0.5rem;
    color: #dc2626;
    font-size: 1.25rem;
  }

  .error-message p {
    margin: 0 0 1rem;
    color: var(--text-secondary, #64748b);
    max-width: 400px;
  }

  .retry-btn {
    padding: 0.75rem 1.5rem;
    background: #dc2626;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
  }

  .retry-btn:hover {
    background: #b91c1c;
    transform: translateY(-2px);
  }

  .no-data-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .no-data-icon {
    margin-bottom: 1rem;
    color: #93c5fd;
  }

  .no-data-icon svg {
    width: 48px;
    height: 48px;
  }

  .no-data-message h3 {
    margin: 0 0 0.5rem;
    color: var(--text-primary, #1e293b);
    font-size: 1.25rem;
  }

  .no-data-message p {
    margin: 0;
    color: var(--text-secondary, #64748b);
  }

  .no-data-hint {
    margin-top: 0.5rem !important;
    font-size: 0.9rem;
    font-style: italic;
  }

  /* ==================== EXECUTIVE SUMMARY ENHANCED ==================== */

  .executive-summary-enhanced {
    padding: 0;
  }

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .summary-header-left h2 {
    margin: 0 0 0.25rem;
  }

  .view-toggle {
    display: flex;
    gap: 0.5rem;
    background: var(--bg-secondary, #f1f5f9);
    padding: 4px;
    border-radius: 12px;
  }

  .toggle-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--text-secondary, #64748b);
    transition: all 0.2s ease;
  }

  .toggle-btn:hover {
    color: var(--text-primary, #1e293b);
  }

  .toggle-btn.active {
    background: white;
    color: #3b82f6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .toggle-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .toggle-icon svg {
    width: 16px;
    height: 16px;
  }

  /* KPI Row */
  .kpi-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .kpi-card {
    background: white;
    border-radius: 16px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid var(--border-color, #e2e8f0);
    transition: all 0.3s ease;
    min-width: 0;
  }

  .kpi-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }

  .kpi-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
  }

  .kpi-sales::before { background: linear-gradient(90deg, #10b981, #34d399); }
  .kpi-net::before { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
  .kpi-deletions::before { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
  .kpi-targets::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }

  .kpi-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  }

  .kpi-icon-wrapper.net {
    background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  }

  .kpi-icon-wrapper.warning {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  }

  .kpi-icon-wrapper.primary {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  }

  .kpi-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .kpi-icon svg {
    width: 24px;
    height: 24px;
  }

  .kpi-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    overflow: hidden;
  }

  .kpi-label {
    font-size: 0.8rem;
    color: var(--text-secondary, #64748b);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .kpi-value {
    font-size: clamp(1.1rem, 2vw, 1.5rem);
    font-weight: 700;
    color: var(--text-primary, #1e293b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .kpi-value.success { color: #10b981; }
  .kpi-value.warning { color: #f59e0b; }
  .kpi-value.danger { color: #ef4444; }

  .kpi-subtitle {
    font-size: 0.75rem;
    color: var(--text-secondary, #94a3b8);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .kpi-mini-chart {
    margin: -0.5rem -0.5rem 0;
  }

  .kpi-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.65rem;
    font-weight: 600;
    max-width: 45%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .kpi-badge.success {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }

  .kpi-badge.warning {
    background: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }

  .kpi-badge.danger {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  .kpi-badge.info {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  .kpi-badge.primary {
    background: rgba(139, 92, 246, 0.1);
    color: #8b5cf6;
  }

  .progress-bar-mini {
    width: 100%;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
    margin-top: 0.25rem;
  }

  .progress-fill-mini {
    height: 100%;
    border-radius: 3px;
    transition: width 0.5s ease;
  }

  /* Metrics Grid */
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .metric-card {
    background: white;
    border-radius: 16px;
    padding: 1.25rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid var(--border-color, #e2e8f0);
    min-width: 0;
    overflow: hidden;
  }

  .metric-card h4 {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
  }

  .metric-chart-container {
    position: relative;
    min-height: 180px;
  }

  .metric-footer {
    display: flex;
    justify-content: space-around;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-color, #e2e8f0);
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .metric-stat {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: var(--text-secondary, #64748b);
    min-width: 0;
    overflow: hidden;
  }

  .metric-stat span:last-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stat-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .stat-dot.success { background: #10b981; }
  .stat-dot.danger { background: #ef4444; }
  .stat-dot.warning { background: #f59e0b; }
  .stat-dot.primary { background: #3b82f6; }
  .stat-dot.purple { background: #8b5cf6; }

  .radial-center-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    margin-top: -10px;
    max-width: 80%;
  }

  .radial-value {
    display: block;
    font-size: clamp(1.1rem, 2vw, 1.5rem);
    font-weight: 700;
    color: var(--text-primary, #1e293b);
  }

  .radial-text {
    display: block;
    font-size: 0.7rem;
    color: var(--text-secondary, #94a3b8);
  }

  /* Productivity Metrics */
  .productivity-metrics {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .prod-metric {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem;
    background: var(--bg-secondary, #f8fafc);
    border-radius: 10px;
    min-width: 0;
    overflow: hidden;
  }

  .prod-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #3b82f6;
  }

  .prod-icon svg {
    width: 20px;
    height: 20px;
  }

  .prod-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
  }

  .prod-value {
    font-size: clamp(0.8rem, 1.5vw, 0.95rem);
    font-weight: 700;
    color: var(--text-primary, #1e293b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .prod-label {
    font-size: 0.65rem;
    color: var(--text-secondary, #94a3b8);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Charts Grid */
  .charts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .chart-card {
    background: white;
    border-radius: 16px;
    padding: 1.25rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid var(--border-color, #e2e8f0);
    min-width: 0;
    overflow: hidden;
  }

  .chart-card.full-width {
    grid-column: 1 / -1;
  }

  .chart-card h4 {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
  }

  .chart-container {
    min-height: 250px;
    position: relative;
  }

  .gauge-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .gauge-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    max-width: 80%;
  }

  .gauge-value {
    display: block;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
    color: var(--text-primary, #1e293b);
  }

  .gauge-label {
    display: block;
    font-size: 0.75rem;
    color: var(--text-secondary, #94a3b8);
  }

  /* Performance Indicators */
  .performance-indicators {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
  }

  .perf-indicator {
    background: white;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid var(--border-color, #e2e8f0);
    min-width: 0;
    overflow: hidden;
  }

  .perf-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    gap: 0.5rem;
  }

  .perf-title {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-secondary, #64748b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }

  .perf-value {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary, #1e293b);
    flex-shrink: 0;
  }

  .perf-value.success { color: #10b981; }
  .perf-value.warning { color: #f59e0b; }
  .perf-value.danger { color: #ef4444; }

  .perf-bar {
    width: 100%;
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 0.5rem;
  }

  .perf-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.5s ease;
  }

  .perf-desc {
    font-size: 0.7rem;
    color: var(--text-secondary, #94a3b8);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Dark mode support */
  [data-theme="dark"] .kpi-card,
  [data-theme="dark"] .metric-card,
  [data-theme="dark"] .chart-card,
  [data-theme="dark"] .perf-indicator {
    background: var(--card-bg, #1e293b);
    border-color: var(--border-color, #334155);
  }

  [data-theme="dark"] .toggle-btn.active {
    background: var(--card-bg, #1e293b);
  }

  [data-theme="dark"] .prod-metric {
    background: rgba(255, 255, 255, 0.05);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .summary-header {
      flex-direction: column;
    }

    .view-toggle {
      width: 100%;
      justify-content: center;
    }

    .kpi-row {
      grid-template-columns: 1fr;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
    }

    .charts-grid {
      grid-template-columns: 1fr;
    }

    .performance-indicators {
      grid-template-columns: 1fr;
    }

    .productivity-metrics {
      grid-template-columns: 1fr;
    }

    .kpi-value {
      font-size: 1.25rem;
    }
  }

  /* Animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .kpi-card, .metric-card, .chart-card, .perf-indicator {
    animation: fadeInUp 0.4s ease forwards;
  }

  .kpi-row .kpi-card:nth-child(1) { animation-delay: 0.05s; }
  .kpi-row .kpi-card:nth-child(2) { animation-delay: 0.1s; }
  .kpi-row .kpi-card:nth-child(3) { animation-delay: 0.15s; }
  .kpi-row .kpi-card:nth-child(4) { animation-delay: 0.2s; }

  .metrics-grid .metric-card:nth-child(1) { animation-delay: 0.25s; }
  .metrics-grid .metric-card:nth-child(2) { animation-delay: 0.3s; }
  .metrics-grid .metric-card:nth-child(3) { animation-delay: 0.35s; }
  .metrics-grid .metric-card:nth-child(4) { animation-delay: 0.4s; }
`;

export default InformesPage;
